import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import {
  ReasonCodeItem,
  ReasonCodes,
  ReasonCodesSchema,
} from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/schemas";
import { DetailsParams } from "features/CenterContent/VehicleDetails/TrainDetails/TrainDetails";
import { HeadedContainerSticky } from "features/CenterContent/VehicleDetails/shared/HeadedContainerSticky";
import {
  SharedGridPadding,
  SharedGridStyleNoPadding,
} from "features/CenterContent/shared/MainContentGrid";
import { FC } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRow,
  Text,
} from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { formatDateString } from "shared/utils/dateTimeUtils";
import { useTheme } from "styled-components";

const tableColumns = ["Tid", "Sted", "BN-kode", "Lok-kode", "Kond-kode"];

type TrainReasonCodesContentProps = {
  data: ReasonCodeItem[];
};
const TrainReasonCodesContent: FC<TrainReasonCodesContentProps> = ({
  data,
}) => {
  const theme = useTheme();
  const getCodeAndComment = (code: string | null, comment: string | null) =>
    [code, comment].filter(Boolean).join(": ");
  return (
    <Table colorScheme="grey" size="sm" width="100%">
      <TableHeader backgroundColor={theme.colorBackgroundSecondary}>
        <TableRow>
          {tableColumns.map((column) => (
            <TableColumnHeader key={column} border="none">
              {column}
            </TableColumnHeader>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((rc) => {
          const isConductor = rc.employeeRole === "CONDUCTOR";
          const isDriver = rc.employeeRole === "DRIVER";
          return (
            <TableRow key={rc.id}>
              <TableCell>
                {formatDateString(rc.registeredAt, "HH:mm")}
              </TableCell>
              <TableCell>
                <p>{rc.stationName}</p>
              </TableCell>
              <TableCell>
                {getCodeAndComment(rc.tiosReasonCode, rc.tiosComment)}
              </TableCell>
              <TableCell>
                {isDriver &&
                  getCodeAndComment(rc.employeeReasonCode, rc.employeeComment)}
              </TableCell>
              <TableCell>
                {isConductor &&
                  getCodeAndComment(rc.employeeReasonCode, rc.employeeComment)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const TrainReasonCodes = () => {
  const { trainNumber, trainDate, countryCode } = useParams<DetailsParams>();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: ["getReasonCodesForTrain", trainNumber, trainDate],
    queryFn: ({ signal }) =>
      axiosClient
        .get<ReasonCodes>(
          `${getBackendUrl()}/reason-codes/by-train-departure?nominalDate=${trainDate}&trainId=${trainNumber}`,
          {
            signal,
          },
        )
        .then((res) => ReasonCodesSchema.parse(res.data)),
    enabled: countryCode === "NO",
  });
  const isLoadingOrNoData = !data || data.length <= 0 || isLoading;
  if (!isError && isLoadingOrNoData) {
    return null;
  }
  return (
    <HeadedContainerSticky
      panelStyle={SharedGridStyleNoPadding}
      headerHeight="54px"
      variant="main"
      header={
        <Text variant="xs" fontWeight={600} mr="auto" p={SharedGridPadding}>
          Årsakskoder
        </Text>
      }
    >
      {isError && <FailureMessage customMessage="" />}
      {isSuccess && <TrainReasonCodesContent data={data} />}
    </HeadedContainerSticky>
  );
};

export default TrainReasonCodes;
